import React, { useEffect, useState } from "react";
import { Box, Button, DialogActions, DialogContent, DialogTitle, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import Chip from "components/uiElements/dataDisplay/Chip";
import { Tag } from "routes/tagManagement/TagManagementPage";
import { ResourceTagEntry } from "routes/tagManagement/TagQueriesDialog";
import TagRequestHelper from "routes/tagManagement/TagRequestHelper";
import TagSelector from "routes/tagManagement/TagSelector";
import { Query } from "system/query/Query";
import SimpleQueryList from "./SimpleQueryList";
import { TextLoader } from "components/apiHandling/TextLoader";
import { APIError } from "system/APIRequest";

interface BulkEditTagsDialogProps {
  onChange(): void;
  onClose(): void;
  queries: Array<Query>;
}

enum BulkEditType {
  Add = 1,
  Remove = 2,
  Replace = 3
}

export default function BulkEditTagsDialog(props: BulkEditTagsDialogProps): React.JSX.Element {
  const { i18n } = useTranslation();

  const [tabIndex, _setTabIndex] = useState<BulkEditType>(BulkEditType.Add);
  const [queryTags, setQueryTags] = useState<Array<Tag>>([]);
  const [resourceTagEntries, setResourceTagEntries] = useState<Array<ResourceTagEntry>>([]);
  const [selectedTags, setSelectedTags] = useState<Array<Tag>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showQueryDialog, setShowQueryDialog] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  function setTabIndex(value: BulkEditType): void {
    _setTabIndex(value);
    setSelectedTags([]);
  }

  async function load(queries: Array<Query>) {
    const tags: Array<Tag> = [];
    const resourceTagEntries: Array<ResourceTagEntry> = [];
    
    setIsLoading(true);
    setQueryTags(tags);
    setResourceTagEntries(resourceTagEntries);

    for (const query of queries) {
      if (query.id) {
        const response = await TagRequestHelper.getTagsForQuery(query.id);
        for (const tag of response.tags) {
          if (tags.some((t) => t.id === tag.id)) {
            // We are already tracking this tag.
            continue;
          }
          tags.push(tag);
        }
        if (response.resourceTagEntries.length > 0) {
          resourceTagEntries.push(...response.resourceTagEntries);
        }
      }
    }

    setQueryTags(tags);
    setResourceTagEntries(resourceTagEntries);
    setIsLoading(false);
  }

  useEffect(() => {
    load(props.queries);
    setSelectedTags([]);
  }, []);

  async function handleRemoveAllTags(): Promise<void> {
    setIsSubmitting(true);
    try{
      await removeTags(props.queries, queryTags, resourceTagEntries);
      enqueueSnackbar("Successfully updated tags.", {variant: "success"});
    } catch (e) {
      const error = e as APIError;
      enqueueSnackbar(error.message, {variant: "error", persist: true});
    } finally {
      setIsSubmitting(false);
      props.onChange();
    }
  }

  async function addTags(queries: Array<Query>, tags: Array<Tag>, resourceTagEntries: Array<ResourceTagEntry>): Promise<void> {
    for (const query of queries) {
      const tagsToAdd = tags.filter((tag) => {
        const queryAlreadyHasTag = resourceTagEntries.some((entry) => {
          return entry.resource_id === query.id && entry.resource_tag_id === tag.id;
        });
        return !queryAlreadyHasTag;
      });
      for (const tag of tagsToAdd) {
        if (query.id) {
          await TagRequestHelper.addTagToQuery(tag.id, query.id);
        }
      }
    }
  }

  async function removeTags(queries: Array<Query>, tags: Array<Tag>, resourceTagEntries: Array<ResourceTagEntry>): Promise<void> {
    for (const query of queries) {
      const tagsToRemove = tags.filter((tag) => {
        const queryHasTag = resourceTagEntries.some((entry) => {
          return entry.resource_id === query.id && entry.resource_tag_id === tag.id;
        });
        return queryHasTag;
      });
      for (const tag of tagsToRemove) {
        if (query.id) {
          await TagRequestHelper.removeTagFromQuery(tag.id, query.id);
        }
      }
    }
  }

  async function replaceTags(queries: Array<Query>, queryTags: Array<Tag>, selectedTags: Array<Tag>, resourceTagEntries: Array<ResourceTagEntry>): Promise<void> {
    const tagsToRemove = queryTags.filter((removingTag) => {
      const keepTag = selectedTags.some((addingTag) => removingTag.id === addingTag.id);
      return !keepTag;
    });

    await removeTags(queries, tagsToRemove, resourceTagEntries);
    await addTags(queries, selectedTags, resourceTagEntries);
  }

  async function handleSubmit(): Promise<void> {
    setIsSubmitting(true);
    try{
      switch (tabIndex) {
        case BulkEditType.Add: {
          await addTags(props.queries, selectedTags, resourceTagEntries);
          break;
        }
        case BulkEditType.Remove: {
          await removeTags(props.queries, selectedTags, resourceTagEntries);
          break;
        }
        case BulkEditType.Replace: {
          await replaceTags(props.queries, queryTags, selectedTags, resourceTagEntries);
          break;
        }
      }
      enqueueSnackbar(i18n.t("bulk-edit-tags-dialog.submit-success"), {variant: "success"});
    } catch (e) {
      const error = e as APIError;
      enqueueSnackbar(error.message, {variant: "error", persist: true});
    } finally {
      setIsSubmitting(false);
      props.onChange();
    }
  }

  return (
    <ResponsiveDialog
      maxWidth="sm"
      open={true}
    >
      <DialogTitle>
        <Typography variant="h4">{i18n.t("bulk-edit-tags-dialog.title")}</Typography>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ backgroundColor: "background.default", p: 1}}
      >
        <Stack sx={{ gap: 1 }}>
          <Paper sx={{ p: 1 }}>
            <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", gap: 1 }}>
              <Typography>
                {i18n.t("bulk-edit-tags-dialog.query-selection-label", {count: props.queries.length})}
              </Typography>
              <Button
                color="primary"
                disabled={isSubmitting}
                onClick={() => setShowQueryDialog(true)}
                size="small"
                variant="outlined">
                  {i18n.t("bulk-edit-tags-dialog.button-view-queries")}
              </Button>
            </Stack>
          </Paper>
          {showQueryDialog &&
            <SimpleQueryList
              onClose={() => setShowQueryDialog(false)}
              queries={props.queries}
              title={i18n.t("bulk-edit-tags-dialog.selected-queries-list")}
            />
          }
          <Paper sx={{ p: 1 }}>
            {queryTags.length > 0 &&
              <Box sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}>
                <Tabs
                  onChange={(event, value) => setTabIndex(value)}
                  value={tabIndex}
                >
                  <Tab disabled={isSubmitting} label={i18n.t("bulk-edit-tags-dialog.tabs.add")} value={BulkEditType.Add} />
                  <Tab disabled={isSubmitting} label={i18n.t("bulk-edit-tags-dialog.tabs.remove")} sx={{display: queryTags.length === 0 ? "none" : ""}} value={BulkEditType.Remove} />
                  <Tab disabled={isSubmitting} label={i18n.t("bulk-edit-tags-dialog.tabs.replace")} sx={{display: queryTags.length === 0 ? "none" : ""}} value={BulkEditType.Replace} />
                </Tabs>
              </Box>
            }
            <Box sx={{ pt: 1 }}>
              {isLoading &&
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <TextLoader />
                </Box>
              }
              {!isLoading &&
                <Stack sx={{ gap: 1 }}>
                  {tabIndex === BulkEditType.Add &&
                    <Typography>{i18n.t("bulk-edit-tags-dialog.flavor-text.add")}</Typography>
                  }
                  {tabIndex === BulkEditType.Remove &&
                    <Typography>{i18n.t("bulk-edit-tags-dialog.flavor-text.remove")}</Typography>
                  }
                  {tabIndex === BulkEditType.Replace &&
                    <Typography>{i18n.t("bulk-edit-tags-dialog.flavor-text.replace")}</Typography>
                  }
                  <Box>
                    <TagSelector
                      disabled={isSubmitting}
                      label={i18n.t("query-management-page.select-tags-label")}
                      onClose={(tags) => {
                        setSelectedTags(tags);
                      }}
                      selectedTags={selectedTags}
                      tags={tabIndex === BulkEditType.Remove ? queryTags : undefined}
                    />
                    <Stack
                      direction="row"
                      sx={{
                        display: selectedTags.length > 0 ? "default" : "none",
                        gap: 1,
                        flexWrap: "wrap",
                        mt: 1
                      }}
                    >
                      {selectedTags.map((tag) => {
                        return <Chip key={tag.id} label={tag.name} color={tag.color} />;
                      })}
                    </Stack> 
                  </Box>
                </Stack>
              }
            </Box>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        {queryTags.length > 0 &&
          <Button
            color="error"
            disabled={isSubmitting}
            onClick={handleRemoveAllTags}
            variant="outlined"
          >
            {i18n.t("bulk-edit-tags-dialog.button-remove-all-tags")}
          </Button>
        }
        <Button
          color="secondary"
          disabled={isSubmitting}
          onClick={() => props.onClose()}
          variant="contained"
        >
          {i18n.t("cancel")}
        </Button>
        <Button
          color="success"
          loading={isSubmitting}
          loadingPosition="start"
          onClick={handleSubmit}
          variant="contained"
        >
          {i18n.t("submit")}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  )
}